import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ExternalLink, Lock } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { adminLogin, adminVerify, getToken, setToken, clearToken } from "../api";
import { useContent } from "../context/ContentContext";
import { HeroHomeEditor, AboutEditor, ContactFooterEditor } from "../components/admin/SiteEditors";
import { VideosEditor, SubmissionsPanel, SubscribersPanel } from "../components/admin/DataPanels";

function LoginScreen({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    try {
      const { token } = await adminLogin(password);
      setToken(token);
      toast.success("Welcome back!");
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#141414] texture-dark flex items-center justify-center px-6">
      <form
        onSubmit={handleLogin}
        className="w-full max-w-sm bg-[#1c1c1c] border border-white/15 rounded-lg p-8"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-3 mb-2">
          <Lock size={18} className="text-white/70" strokeWidth={1.5} />
          <h1 className="font-comfortaa text-white text-lg">Studio Admin</h1>
        </div>
        <p className="font-comfortaa text-white/60 text-xs mb-6">
          Enter your admin password to manage site content.
        </p>
        <Input
          type="password"
          placeholder="Admin password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="bg-[#0d0d0d] border-white/25 text-white font-comfortaa placeholder:text-white/35"
          data-testid="admin-password-input"
        />
        <Button
          type="submit"
          disabled={busy}
          className="w-full mt-4 bg-white text-black hover:bg-white/85 font-comfortaa"
          data-testid="admin-login-button"
        >
          {busy ? "Signing in…" : "Sign In"}
        </Button>
        <p className="font-comfortaa text-white/35 text-[11px] mt-5 text-center">
          Default password: daviddoes2025
        </p>
      </form>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const { refresh } = useContent();

  useEffect(() => {
    const check = async () => {
      if (!getToken()) {
        setChecking(false);
        return;
      }
      try {
        await adminVerify();
        setAuthed(true);
      } catch {
        clearToken();
      } finally {
        setChecking(false);
      }
    };
    check();
  }, []);

  const logout = () => {
    clearToken();
    setAuthed(false);
    toast.success("Signed out");
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#141414] flex items-center justify-center">
        <p className="font-comfortaa text-white/60 text-sm">Loading…</p>
      </div>
    );
  }

  if (!authed) return <LoginScreen onSuccess={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-[#141414] texture-dark" data-testid="admin-dashboard">
      {/* Top bar */}
      <div className="border-b border-white/15 bg-[#0f0f0f]/90 sticky top-0 z-40 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <span className="font-comfortaa font-medium tracking-[0.25em] text-white text-sm">
              DAVIDDOESAI
            </span>
            <span className="font-comfortaa text-white/50 text-xs ml-3">Content Manager</span>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="font-comfortaa text-xs text-white/70 hover:text-white flex items-center gap-1.5"
              style={{ transition: "color 0.25s ease" }}
              data-testid="admin-view-site"
            >
              <ExternalLink size={14} strokeWidth={1.5} /> View site
            </Link>
            <button
              onClick={logout}
              className="font-comfortaa text-xs text-white/70 hover:text-white flex items-center gap-1.5"
              style={{ transition: "color 0.25s ease" }}
              data-testid="admin-logout"
            >
              <LogOut size={14} strokeWidth={1.5} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <Tabs defaultValue="home" className="w-full">
          <TabsList className="bg-[#1c1c1c] border border-white/10 flex-wrap h-auto">
            <TabsTrigger value="home" className="font-comfortaa text-xs data-[state=active]:bg-white data-[state=active]:text-black text-white/70" data-testid="tab-home">Hero & Home</TabsTrigger>
            <TabsTrigger value="about" className="font-comfortaa text-xs data-[state=active]:bg-white data-[state=active]:text-black text-white/70" data-testid="tab-about">About</TabsTrigger>
            <TabsTrigger value="videos" className="font-comfortaa text-xs data-[state=active]:bg-white data-[state=active]:text-black text-white/70" data-testid="tab-videos">Videos</TabsTrigger>
            <TabsTrigger value="contact" className="font-comfortaa text-xs data-[state=active]:bg-white data-[state=active]:text-black text-white/70" data-testid="tab-contact">Contact & Footer</TabsTrigger>
            <TabsTrigger value="submissions" className="font-comfortaa text-xs data-[state=active]:bg-white data-[state=active]:text-black text-white/70" data-testid="tab-submissions">Submissions</TabsTrigger>
            <TabsTrigger value="subscribers" className="font-comfortaa text-xs data-[state=active]:bg-white data-[state=active]:text-black text-white/70" data-testid="tab-subscribers">Subscribers</TabsTrigger>
          </TabsList>

          <TabsContent value="home" className="mt-6"><HeroHomeEditor onSaved={refresh} /></TabsContent>
          <TabsContent value="about" className="mt-6"><AboutEditor onSaved={refresh} /></TabsContent>
          <TabsContent value="videos" className="mt-6"><VideosEditor onSaved={refresh} /></TabsContent>
          <TabsContent value="contact" className="mt-6"><ContactFooterEditor onSaved={refresh} /></TabsContent>
          <TabsContent value="submissions" className="mt-6"><SubmissionsPanel /></TabsContent>
          <TabsContent value="subscribers" className="mt-6"><SubscribersPanel /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
